/**
 * LoanOcrolusPanel — the "Ocrolus" tab body on the loan drilldown Documents tab.
 *
 * Ocrolus is the bank-statement analysis vendor. You create a BOOK for the loan,
 * send bank statements into it, and Ocrolus returns parsed accounts, cash-flow
 * analytics and fraud signals. That output is what corroborates a borrower's
 * stated liquidity, so it feeds the Liquidity Calc downstream.
 *
 * Layout: a card stack for the work (statements + send, per-document
 * authenticity, the system-written bank analysis note, the account-wide book
 * browser) with a status rail on the right
 * that answers "where is this loan's book at?" without scrolling. The send
 * action lives here now — it needs selected rows, and this surface has its own
 * statement table — while the Uploaded tab's bulk bar keeps working as a second
 * entry point.
 *
 * Endpoints (all on the /files prefix — the backend FE doc writes /loan_files,
 * which is wrong; see eastview-os frontend_documentation/ocrolus-book-and-notes.md):
 *   POST /files/create_ocrolus_book/{dealId}        — idempotent create
 *   POST /files/bank_statements_to_ocrolus/{dealId} — send selected statements
 *   GET  /files/ocrolus_book/{dealId}               — status (this panel's spine)
 *   GET  /files/ocrolus_books                       — account-wide browser
 *   POST /files/assign_ocrolus_book/{dealId}        — attach an existing book
 * plus the loan's system-written `ocrolus` note from GET /notes/{dealId}.
 */
import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Box from "@mui/material/Box";

import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import colors from "../../../../../theme/colors";
import OcrolusAuthenticityCard from "./OcrolusAuthenticityCard";
import OcrolusBankStatementsCard from "./OcrolusBankStatementsCard";
import OcrolusBookCard from "./OcrolusBookCard";
import OcrolusBooksTable from "./OcrolusBooksTable";
import OcrolusNoteCard from "./OcrolusNoteCard";
import { SURFACE_BG } from "./ocrolusShared";

const LoanOcrolusPanel = ({
  dealId,
  book,
  state,
  loading,
  error,
  reload,
  // Switches the Documents tab back to the Uploaded view — where statements are
  // uploaded and filed, and where the original bulk send bar lives.
  onGoToUploaded,
}) => {
  const { createOcrolusBook } = useLoanFilesHook();
  const loanDocuments = useSelector(
    (state_) => state_.loanDrilldown?.loanDocuments,
  );
  const [creating, setCreating] = useState(false);
  // Creating a book is a write to a SHARED vendor account (both portals live on
  // one Ocrolus tenant), so it's confirmed rather than fired on a single click.
  const [confirmCreate, setConfirmCreate] = useState(false);
  // Ocrolus doesn't report which documents ended up in a book — the status
  // endpoint returns a COUNT (`filesSent`), not a file list. So the statements
  // table can only mark what was sent from this screen, this session. If the
  // backend ever returns `files_processed` ids, key the column off that instead.
  const [sentDocIds, setSentDocIds] = useState(() => new Set());
  const authenticityRef = useRef(null);

  const handleCreate = useCallback(() => {
    if (!dealId) return;
    setCreating(true);
    createOcrolusBook({
      dealId,
      onSuccessFn: () => {
        setCreating(false);
        setConfirmCreate(false);
        reload?.();
      },
      onFailFn: () => setCreating(false),
    });
    // createOcrolusBook is re-created each render (the hook isn't memoized).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, reload]);

  const requestCreate = useCallback(() => setConfirmCreate(true), []);

  // The status card's authenticity band scrolls to the per-document list. A
  // book graded before 2026-10-15 has no list, but the card still renders (with
  // its re-sync hint), so the target is the same either way.
  const scrollToAuthenticity = useCallback(() => {
    authenticityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const handleSent = useCallback(
    (ids) => {
      setSentDocIds((prev) => new Set([...prev, ...(ids || [])]));
      reload?.();
    },
    [reload],
  );

  const note = book?.ocrolusNote;
  const signals = book?.signals;

  if (loading && !book) {
    return (
      <Flx ac jc sx={{ p: 6 }}>
        <CircularProgress size={22} />
      </Flx>
    );
  }


  return (
    // Capped and centred: the Documents tab runs the full dashboard width, and
    // past ~1800px the statement rows stretch into a run of whitespace between
    // the file name and its dates.
    // The canvas is full-bleed — it's the page ground, so it has to run edge to
    // edge of the tab body. Only the CONTENT is capped at 1800px, otherwise the
    // background stops with the cards and leaves white gutters beside them.
    <Box
      sx={{
        // A faint canvas so the white cards read as cards. Neutral page ground,
        // not an accent — the colors.js rule governs brand/status color.
        background: SURFACE_BG,
        width: "100%",
        flexGrow: 1,
        minHeight: "100%",
      }}
    >
    <Flx
      column
      g={3}
      sx={{
        p: 3,
        width: "100%",
        maxWidth: 1800,
        mx: "auto",
      }}
    >
      {error && (
        <Alert severity="error" sx={{ fontSize: 13 }}>
          Couldn't load the Ocrolus book status for this loan.
        </Alert>
      )}

      {/* Two columns on desktop, stacked below ~1200px: the work on the left,
          the status rail on the right. */}
      <Flx
        g={3}
        sx={{
          alignItems: "flex-start",
          flexDirection: { xs: "column-reverse", lg: "row" },
        }}
      >
        <Flx column g={3} sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <OcrolusBankStatementsCard
            dealId={dealId}
            documents={loanDocuments}
            state={state}
            filesSent={book?.filesSent}
            creating={creating}
            onCreateBook={requestCreate}
            sentDocIds={sentDocIds}
            onSent={handleSent}
            onGoToUploaded={onGoToUploaded}
          />

          {signals && (
            <Box
              ref={authenticityRef}
              sx={{ scrollMarginTop: "calc(var(--drilldown-nav-h, 0px) + 16px)" }}
            >
              <OcrolusAuthenticityCard
                signals={signals}
                loanDocuments={loanDocuments}
                onResync={requestCreate}
              />
            </Box>
          )}

          <OcrolusNoteCard dealId={dealId} noteStamp={note?.updatedAt} />

          <OcrolusBooksTable dealId={dealId} onAssigned={reload} />
        </Flx>

        {/* The rail is `position: sticky` (see OcrolusBookCard) — it needs a
            plain block wrapper with no height of its own to stick against. */}
        <Box
          sx={{ width: { xs: "100%", lg: 380 }, flexShrink: 0, alignSelf: "stretch" }}
        >
          <OcrolusBookCard
            dealId={dealId}
            book={book}
            state={state}
            loading={loading}
            creating={creating}
            onCreateBook={requestCreate}
            onRefresh={reload}
            onViewSignals={signals ? scrollToAuthenticity : undefined}
          />
        </Box>
      </Flx>

      {/* Create / re-sync confirmation. The call is idempotent server-side, so
          this isn't guarding against a duplicate book — it's guarding against an
          accidental write to the shared Ocrolus account, and it's where the
          naming rule gets stated before the book is named. */}
      <Dialog
        open={confirmCreate}
        onClose={() => (creating ? null : setConfirmCreate(false))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Txt sx={{ fontSize: 14, fontWeight: 600, color: colors.ink }}>
            {state === "none"
              ? "Create an Ocrolus book?"
              : "Re-sync this loan's Ocrolus book?"}
          </Txt>
        </DialogTitle>
        <DialogContent>
          <Txt sx={{ fontSize: 12.5, color: colors.slate600 }} component="div">
            {state === "none" ? (
              <>
                This creates a book on the Ocrolus account for this loan, named
                after its EV/LQ number (or the loan number when it has none). It
                is anchored to this loan, so statements sent from here land in
                it and the analysis comes back to this screen.
              </>
            ) : (
              <>
                This loan already has a book. The call is idempotent — it
                returns the existing book rather than creating a second one, and
                refreshes the status shown here.
              </>
            )}
          </Txt>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            disabled={creating}
            onClick={() => setConfirmCreate(false)}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating || !dealId}>
            {creating ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : state === "none" ? (
              "Create book"
            ) : (
              "Re-sync book"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Flx>
    </Box>
  );
};

export default LoanOcrolusPanel;
